<template>
  <div style="display: inline-block">
    <v-menu
    v-show="dateConfiguration.selectedPayPeriod !== undefined"
    bottom
    left>
      <template #activator="{ on, attrs}">
        <v-icon
        id="settlement-action-menu-button"
        color="primary"
        :disabled="disabled"
        v-bind="attrs"
        v-on="on">mdi-dots-vertical</v-icon>
      </template>
      <v-list id="settlement-action-menu" :class="listContainerClass">
        <v-list-item
        v-for="(action, index) in actions"
        :key="`accounting-action-${index}`"
        :disabled="action.disabled"
        @click="action.onClick(action.clickArgument)"
        :id="action.id">
          <v-avatar left >
            <v-icon
            :color="action.isEnabled ? 'grey' : 'black'">
              {{action.icon}}
            </v-icon>
          </v-avatar>
          <v-list-item-title class="subtitle-1">
            {{action.text}}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <Dialog :stateId="columnAdjusterDialogId" fullscreenBreakPoint="smAndDown">
      <SettlementsColumnAdjusterCard
      :isByProducts="isByProducts"
      :mobileMode="$vuetify.breakpoint.smAndDown"
      @apply="closeColumnAdjuster"
      @close="closeColumnAdjuster"/>
    </Dialog>
    <v-dialog v-model="csvDialog" width="400">
      <CSVExport
      v-if="csvDialog"
      :isByProducts="isByProducts"
      @close-csv-export="csvDialog = false"/>
    </v-dialog>
    <v-dialog v-model="settlementMessageDialog" width="500" persistent>
      <v-card v-if="settlementMessageDialog">
        <v-card-title class="primary headline white--text">
          <span>{{ $t('editSettlementMessage') }}</span>
          <v-spacer/>
          <Icon
          icon="mdi-close"
          iconColor="white"
          :small="false"
          large
          :tooltipText="$t('cancel')"
          @icon-clicked="closeSettlementMessageDialog"/>
        </v-card-title>
        <v-card-text class="mt-6">
          <FormWrapper
          @submit="saveSettlementMessage"
          :buttonText="$t('save')"
          :disabled="savingSettlementMessage"
          strongValidation>
            <v-textarea
            v-model="settlementMessageDraft"
            :label="$t('settlementMessage')"
            :hint="$t('settlementMessageHint')"
            :rules="[rules.required]"
            outlined
            persistent-hint
            counter
            maxlength="512"
            rows="4"
            auto-grow
            autofocus/>
          </FormWrapper>
        </v-card-text>
      </v-card>
    </v-dialog>
    <Dialog :stateId="dialogId" maxWidth="800">
      <SettlementSheetPayeeSelector
      v-if="bulkSettlementDeliveryDialog"
      :deliveryMethod="settlementSheetDeliveryMethod"
      :emailScaleData="emailScaleData"
      @close="close"/>
    </Dialog>
  </div>
</template>

<script>
import { generateZippedSettlementReport } from '@/utils/pdf-formatters/ZippedSettlementsReport.js'
import { generateSinglePDFSettlementReport } from '@/utils/pdf-formatters/SinglePDFSettlementsReport.js'
import { createProductionSummaryPDF } from '@/utils/pdf-formatters/ProductionSummaryReport.js'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { PayPeriodStatus, FinancialIntegrationType, ErrorSource, SettlementArchiveStatus, BulkSettlementDeliveryMode, SettlementEmailMessageMode, UserClaims } from '@/utils/Enumerations.js'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import saveAs from 'file-saver'
import { fileNameForString } from '@/utils/pdf-formatters/GenericSettlementsFunctions.js'
import { SettlementKeys, SettlementSubKeys } from '@/components/settlements/SettlementKeys.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { kebabCase } from 'lodash'
import { sanitizeSettlementMessage, containsUrl } from '@/utils/SettlementMessageSanitizer.js'
import fieldRules from '@/utils/rules.js'

export default {
  name: 'SettlementActionMenu',

  components: {
    CSVExport: () => import('@/components/settlements/CSVExport.vue'),
    SettlementSheetPayeeSelector: () => import('@/components/settlements/SettlementSheetPayeeSelector.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    SettlementsColumnAdjusterCard: () => import('@/components/settlements/SettlementsColumnAdjusterCard.vue')
  },

  props: {
    isByProducts: {
      type: Boolean,
      required: true
    },
    isExporter: {
      type: Boolean,
      default: false
    },
    containerClass: {
      type: String,
      requried: false,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    csvDialog: false,
    bulkSettlementDeliveryDialog: false,
    zippedPdf: false,
    showRebuild: false,
    bulkEmail: false,
    emailScaleData: false,
    settlementSheetDeliveryMethod: undefined,
    dialogId: uniqueDialogId('payeeSelector'),
    settlementMessageDialog: false,
    settlementMessageDraft: '',
    savingSettlementMessage: false,
    rules: fieldRules.rules,
    columnAdjusterDialogId: uniqueDialogId('settlements-column-adjuster')
  }),

  computed: {
    ...mapGetters('settlements', ['currentModuleKey', 'currentSubModuleKey', 'dateConfiguration', 'currentSubModule']),
    ...mapGetters('user', ['companyInfo']),

    listContainerClass () {
      return `${this.containerClass} ${kebabCase(this.currentModuleKey)} ${kebabCase(this.currentSubModuleKey)}`
    },

    showIntegrationsOption () {
      return this.dateConfiguration?.exportBatch?.exportBatchId !== undefined &&
        (this.companyInfo.financialIntegrationType === FinancialIntegrationType.BusinessCentralWithBinaryStream.value ||
          this.companyInfo.financialIntegrationType === FinancialIntegrationType.CsvDrop.value || !this.isNullOrUndefined(this.dateConfiguration?.exportBatch?.secondaryBiPumpStatus))
    },

    showSettlementMessageOption () {
      return this.dateConfiguration?.exportBatch?.exportBatchId !== undefined &&
        this.companyInfo?.settlementEmailMessageMode === SettlementEmailMessageMode.Default.value &&
        userAssignedClaim(UserClaims.ExportManager)
    },

    showCsvOption () {
      return this.dateConfiguration?.exportBatch?.exportBatchId !== undefined &&
        (this.companyInfo.financialIntegrationType === FinancialIntegrationType.CsvDownloads.value ||
        this.companyInfo.financialIntegrationType === FinancialIntegrationType.CsvDrop.value)
    },

    showColumnAdjusterOption () {
      if (this.isByProducts) return this.currentModuleKey === SettlementKeys.AccountsPayable || this.currentModuleKey === SettlementKeys.AccountsReceivable
      return this.currentModuleKey === SettlementKeys.AccountsPayable || this.currentModuleKey === SettlementKeys.Accruals
    },

    pdfDownloadDisabled () {
      if (this.currentSubModule === undefined) return true

      switch (this.currentSubModuleKey) {
        case 'settlementsByContract': return this.currentSubModule.settlements.length === 0
        case 'settlementsByPayee':
          if (this.currentModuleKey === 'accruals') {
            return true
          } else {
            return this.currentSubModule.payees.length === 0
          }
        case 'byproductSettlementsByPayee': return this.currentSubModule.payees.length === 0
        case 'productionSummary': return this.currentModuleKey !== 'accountsPayable'
        case 'register':
        case 'bpRegister': return !this.dateConfiguration.exportBatch.exportBatchId || this.currentSubModule.journalEntries.length === 0
        case 'receivableRegister':
        case 'bpReceivableRegister': return !this.dateConfiguration.exportBatch.exportBatchId || this.currentSubModule.businessEntities.length === 0
        case 'bpPaymentRegister':
        case 'paymentRegister': return !this.dateConfiguration.exportBatch.exportBatchId || this.currentSubModule.payments.length === 0
        default: return true
      }
    },

    singlePdfFileText () {
      switch (this.currentModuleKey) {
        case 'accountsPayable':
          switch (this.currentSubModuleKey) {
            case 'bpPaymentRegister':
            case 'paymentRegister': return this.$t('paymentRegisterPDF')
            case 'productionSummary': return this.$t('productionSummaryPDF')
            case 'byproductSettlementsByPayee':
            case 'settlementsByContract':
            case 'settlementsByPayee': return this.$t('settlementStatementPDF')
          }
        // eslint-disable-next-line no-fallthrough
        case 'accountsReceivable':
          switch (this.currentSubModuleKey) {
            case 'receivableRegister':
            case 'bpReceivableRegister': return this.$t('receivableRegisterPDF')
            case 'productionSummary': return this.$t('receivableSummaryPDF')
          }
        // eslint-disable-next-line no-fallthrough
        case 'accruals':
          switch (this.currentSubModuleKey) {
            case 'register':
            case 'bpRegister': return this.$t('registerPDF')
          }
        // eslint-disable-next-line no-fallthrough
        default: return this.$t('singlePDFFile')
      }
    },

    rateVerificationDisabled () {
      if (this.currentSubModule === undefined || this.currentModuleKey === undefined) return true
      if (this.currentSubModuleKey === 'settlementsByContract' && this.currentModuleKey === 'accountsPayable') return this.currentSubModule.settlements.length === 0
      if ((this.currentSubModuleKey === 'settlementsByPayee' || this.currentSubModuleKey === 'byproductSettlementsByPayee') && this.currentModuleKey === 'accountsPayable') return !this.currentSubModule.payees.some(p => p.contracts.length > 0)
      if (this.currentSubModuleKey === 'settlementsByPayee' && this.currentModuleKey === 'accruals') return !this.currentSubModule.payees.some(p => p.contracts.length > 0)

      return true
    },

    actions () {
      let isExportDisabled = true
      let exportGenComplete = true
      const { payPeriod, exportBatch } = this.dateConfiguration
      if (payPeriod !== undefined && exportBatch !== undefined) {
        isExportDisabled = (payPeriod.status !== PayPeriodStatus.Closed.value) || !this.isExporter
        exportGenComplete = exportBatch.settlementArchiveStatus === SettlementArchiveStatus.Success.value || exportBatch.exportBatchId === undefined
      }

      const menuOptions = [
        {
          text: this.$t('zippedPDFFolder'),
          id: 'zipped-pdf-folder',
          disabled: this.pdfDownloadDisabled || !exportGenComplete || (this.currentSubModuleKey !== 'settlementsByContract' && this.currentSubModuleKey !== 'settlementsByPayee' && this.currentSubModuleKey !== 'byproductSettlementsByPayee'),
          icon: 'mdi-folder-zip-outline',
          onClick: this.downloadPDFReport,
          clickArgument: true
        },
        {
          text: this.singlePdfFileText,
          id: `${this.currentSubModuleKey}-single-pdf-file`,
          disabled: this.pdfDownloadDisabled || !(exportGenComplete),
          icon: 'mdi-file-pdf-box',
          onClick: this.downloadPDFReport,
          clickArgument: false
        }
      ]

      if (exportBatch?.exportBatchId !== undefined) {
        menuOptions.push(
          {
            text: this.$t('bulkEmailDelivery'),
            id: 'bulk-email-delivery',
            disabled: this.pdfDownloadDisabled || !exportGenComplete || (this.currentSubModuleKey !== 'settlementsByContract' && this.currentSubModuleKey !== 'settlementsByPayee' && this.currentSubModuleKey !== 'byproductSettlementsByPayee'),
            icon: 'mdi-email-multiple',
            onClick: this.bulkEmailDialog
          }
        )
        menuOptions.push(
          {
            text: this.$t('emailScaleSheets'),
            id: 'email-scale-sheets',
            disabled: !exportGenComplete || (this.currentSubModuleKey !== 'settlementsByContract' && this.currentSubModuleKey !== 'settlementsByPayee'),
            icon: 'mdi-email',
            onClick: this.openBulkEmailDialogForScaleSheets
          }
        )
      }

      menuOptions.push(
        {
          text: this.$t('rateVerification'),
          id: 'rate-verification',
          icon: 'mdi-check-decagram',
          onClick: this.viewRateVerification,
          disabled: this.rateVerificationDisabled
        }
      )

      if (this.showCsvOption) {
        menuOptions.push(
          {
            text: this.$t('CSV'),
            id: 'csv',
            icon: 'mdi-file-table-box',
            onClick: this.openCSVExport
          }
        )
      }

      if (this.showIntegrationsOption) {
        menuOptions.push(
          {
            text: this.$t('integrations'),
            id: 'integrations',
            icon: 'mdi-cloud-sync',
            onClick: this.openIntegrationsDialog
          }
        )
      }

      if (this.showSettlementMessageOption) {
        menuOptions.push(
          {
            text: this.$t('editSettlementMessage'),
            id: 'edit-settlement-message',
            icon: 'mdi-message-text-outline',
            onClick: this.openSettlementMessageDialog
          }
        )
      }

      if (!this.isByProducts) {
        menuOptions.push(
          {
            text: this.getExportButtonText(),
            id: 'export-pay-period',
            disabled: isExportDisabled,
            icon: 'mdi-export',
            onClick: this.exportPayPeriod
          },
          {
            text: this.$t('ticketsInPeriodPDF'),
            icon: 'mdi-printer',
            onClick: this.openTicketPDFPrint,
            id: 'print-tickets'
          }
        )
      }

      const rebuildAvailable = exportBatch?.settlementArchiveStatus === SettlementArchiveStatus.Success.value || exportBatch?.settlementArchiveStatus === SettlementArchiveStatus.Error.value
      if (rebuildAvailable && this.showRebuild) {
        menuOptions.push({
          text: this.$t('rebuildArchiveData'),
          icon: 'mdi-bulldozer',
          onClick: this.rebuildArchiveData
        })
      }

      if (this.showColumnAdjusterOption) {
        menuOptions.push({
          text: this.$t('adjustColumns'),
          id: 'adjust-columns',
          icon: 'mdi-application-edit',
          onClick: this.openColumnAdjuster
        })
      }
      return menuOptions
    }
  },

  created () {
    document.addEventListener('keydown', event => {
      if (event.ctrlKey && event.keyCode === 71) this.showRebuild = !this.showRebuild
    })
  },

  beforeDestroy () {
    document.removeEventListener('keydown', document)
  },

  methods: {
    kebabCase,
    ...mapActions('settlements', ['fetchByproductSettlementsByPayee', 'fetchSettlementsByPayee', 'downloadSubmoduleBlob', 'refreshModuleData']),
    ...mapActions('export-batch', ['patchExportBatch', 'fetchExportBatches']),
    ...mapMutations('global', ['setLoading']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    isNullOrUndefined (v) {
      return v === null || v === undefined
    },
    openColumnAdjuster () {
      this.openOrUpdateDialog({ id: this.columnAdjusterDialogId, width: '800px' })
    },
    closeColumnAdjuster () {
      this.closeDialogsAtOrAbove(this.columnAdjusterDialogId)
    },
    async downloadPDFReport (zipped) {
      switch (this.currentSubModuleKey) {
        case 'productionSummary':
          createProductionSummaryPDF(this.currentSubModule)
          break
        case 'settlementsByContract':
        case 'settlementsByPayee':
          this.handleSettlementSheetPdf(zipped)
          break
        case 'byproductSettlementsByPayee':
          this.handleSettlementSheetPdf(zipped)
          break
        case 'register':
        case 'bpRegister':
        case 'receivableRegister':
        case 'bpReceivableRegister':
        case 'bpPaymentRegister':
        case 'paymentRegister':
          this.handleRegisterPdf()
          break
        default:
          break
      }
    },

    async performLoadingAction (asyncLoadingAction) {
      this.setLoading(true)
      try {
        await asyncLoadingAction()
      } catch (error) {
        console.error(error)
        this.setSnackError({
          message: error.message,
          code: 'NO_DATA',
          source: ErrorSource.WebClient
        })
      } finally {
        this.setLoading(false)
      }
    },

    openCSVExport () {
      if (this.dateConfiguration.payPeriod === undefined) {
        return
      }

      if (this.dateConfiguration.exportBatch !== undefined) {
        this.csvDialog = true
        return
      }

      this.setSnackError(this.$t('unavailableCSV'))
    },

    exportPayPeriod () {
      this.$emit('export-button-pressed')
    },

    openTicketPDFPrint () {
      this.$emit('open-ticket-pdf-print')
    },

    openIntegrationsDialog () {
      this.$emit('open-integrations-dialog')
    },

    getExportButtonText () {
      if (this.dateConfiguration.payPeriod) {
        switch (this.dateConfiguration.payPeriod.status) {
          case PayPeriodStatus.Closed.value:
            return this.$t('exportPayPeriod')
          case PayPeriodStatus.Open.value:
            return this.$t('cannotExport')
          case PayPeriodStatus.Inactive.value:
            return this.$t('notAvailable')
          case PayPeriodStatus.Exported.value:
            return this.$t('exported')
          default:
            return 'EXCEPTION'
        }
      } else {
        return this.$t('cannotExport')
      }
    },

    viewRateVerification () {
      this.$emit('open-rate-verification-dialog')
    },

    rebuildArchiveData () {
      this.$emit('rebuild-archive-data', this.dateConfiguration?.exportBatch)
    },

    handleSettlementSheetPdf (zipped) {
      const exportBatch = this.dateConfiguration?.exportBatch
      if (exportBatch.exportBatchId !== null && exportBatch.exportBatchId !== undefined) {
        this.settlementSheetDeliveryMethod = zipped ? BulkSettlementDeliveryMode.ZippedFolder : BulkSettlementDeliveryMode.SinglePdf
        this.bulkSettlementDeliveryDialog = true
        this.openOrUpdateDialog({ id: this.dialogId, width: 400 })
      } else {
        this.performLoadingAction(async () => {
          const settlementsByPayee = this.isByProducts ? await this.fetchByproductSettlementsByPayee() : await this.fetchSettlementsByPayee()
          const { blob, filename } = zipped
            ? await generateZippedSettlementReport(settlementsByPayee, { exportBatch })
            : await generateSinglePDFSettlementReport(settlementsByPayee, { exportBatch })
          saveAs(blob, filename)
        })
      }
    },

    bulkEmailDialog (emailScaleData = false) {
      this.settlementSheetDeliveryMethod = BulkSettlementDeliveryMode.Email
      this.emailScaleData = emailScaleData
      this.bulkSettlementDeliveryDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: 400 })
    },

    openBulkEmailDialogForScaleSheets () {
      this.bulkEmailDialog(true)
    },

    async handleRegisterPdf () {
      const { exportBatch } = this.dateConfiguration
      let moduleSegment = ''
      switch (this.currentSubModuleKey) {
        case (SettlementSubKeys.AP.PaymentRegister):
        case (SettlementSubKeys.AP.BPPaymentRegister):
          moduleSegment = this.$t('payables')
          break
        case (SettlementSubKeys.AR.ReceivableRegister):
        case (SettlementSubKeys.AR.BPReceivableRegister):
          moduleSegment = this.$t('receivables')
          break
        case (SettlementSubKeys.AC.Register):
        case (SettlementSubKeys.AC.BPRegister):
          moduleSegment = this.$t('accruals')
      }
      const filename = `${this.$t(this.isByProducts ? 'byproducts' : 'logs')}${moduleSegment}_${fileNameForString(exportBatch.label)}`
      await this.downloadSubmoduleBlob({ accountId: undefined, filename })
    },

    async close ({ refresh = false }) {
      this.emailScaleData = false
      this.bulkSettlementDeliveryDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
      this.settlementSheetDeliveryMethod = undefined
      if (refresh) await this.refreshModuleData()
    },

    openSettlementMessageDialog () {
      this.settlementMessageDraft = this.dateConfiguration?.exportBatch?.settlementMessage ?? ''
      this.settlementMessageDialog = true
    },

    closeSettlementMessageDialog () {
      this.settlementMessageDialog = false
      this.settlementMessageDraft = ''
    },

    async saveSettlementMessage () {
      const { payPeriod, exportBatch } = this.dateConfiguration
      if (!exportBatch?.exportBatchId) return
      const sanitized = sanitizeSettlementMessage(this.settlementMessageDraft)
      if (containsUrl(sanitized)) {
        this.setSnackError(this.$t('settlementMessageNoUrls'))
        return
      }
      this.savingSettlementMessage = true
      try {
        await this.patchExportBatch({
          exportBatchId: exportBatch.exportBatchId,
          body: [{ op: 'add', path: '/settlementMessage', value: sanitized }]
        })
        await this.fetchExportBatches(payPeriod.payPeriodId)
        this.setSnack(this.$t('settlementMessageSaved'))
        this.closeSettlementMessageDialog()
      } finally {
        this.savingSettlementMessage = false
      }
    }
  }
}
</script>
